// @greenlight-spec: 59-platform-agent.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Play } from 'lucide-react';
import { toast } from 'sonner';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { LiveStepLine, useCycle } from '@/components/agents/Transcript';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { SegmentedTabs } from '@/components/greenlight/SegmentedTabs';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';

import { SESSIONS, type SessionDef, type SessionStatus } from './fixtures';
import {
  Ago,
  AgentTile,
  RoadmapTag,
  SessionStatusBadge,
  TriggerChip,
  agentById,
  kindById,
  usd,
} from './shared';
import { DefinitionCard } from './DefinitionCard';
import { StartSessionDialog } from './StartSessionDialog';

export function AgentDetailPage(): React.JSX.Element {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const agent = agentById(agentId);
  usePageTitle(agent?.name ?? 'Agent');
  const navigate = useNavigate();
  const [enabled, setEnabled] = React.useState(agent?.enabled ?? false);
  const [filter, setFilter] = React.useState<'all' | SessionStatus>('all');
  const [starting, setStarting] = React.useState(false);
  const roadmap = agent?.status === 'roadmap';

  if (!isPreviewFeatureEnabled('greenlight-agents')) return <Navigate to="/apps" replace />;
  if (!agent) return <EmptyState title="No such agent" />;

  const sessions = SESSIONS.filter((s) => s.agentId === agent.id).sort((a, b) =>
    b.startedAt.localeCompare(a.startedAt),
  );
  const shown = sessions.filter((s) => filter === 'all' || s.status === filter);

  return (
    <div className="space-y-5">
      <Link
        to="/agents"
        className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" aria-hidden /> Greenlight Agents
      </Link>
      <PageHeader
        eyebrow={roadmap ? 'Roadmap' : enabled ? 'Enabled' : 'Disabled'}
        title={agent.name}
        actions={
          <>
            <span className="flex items-center gap-2 text-[13px]">
              <Switch
                disabled={roadmap}
                checked={enabled && !roadmap}
                onCheckedChange={(v) => {
                  setEnabled(v);
                  toast.success(`${agent.name} ${v ? 'enabled' : 'disabled'}`);
                }}
                aria-label="Enabled"
              />
              Enabled
            </span>
            <Button onClick={() => setStarting(true)} disabled={!enabled && !roadmap}>
              <Play aria-hidden /> {agent.startLabel}
            </Button>
          </>
        }
      />
      <div className="flex max-w-3xl items-start gap-3">
        <AgentTile agentId={agent.id} muted={!enabled || roadmap} />
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          {roadmap && <RoadmapTag className="mr-2 align-[1px]" />}
          {agent.description}
        </p>
      </div>
      <StartSessionDialog open={starting} onOpenChange={setStarting} agentId={agent.id} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <SectionCard
            title="Sessions"
            headerBar
            badge={
              <SegmentedTabs
                size="sm"
                items={[
                  { value: 'all', label: `All ${sessions.length}` },
                  { value: 'waiting', label: 'Waiting' },
                  { value: 'failed', label: 'Failed' },
                ]}
                value={filter}
                onValueChange={(v) => setFilter(v as 'all' | SessionStatus)}
                aria-label="Session filter"
              />
            }
          >
            {shown.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  title="No sessions yet"
                  description="Sessions appear here when a trigger fires or you start one."
                />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {shown.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/agents/${agent.id}/sessions/${s.id}`)}
                      className="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-accent/40"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-medium">{s.title}</span>
                        <SessionLine session={s} />
                        <span className="mt-0.5 block">
                          <TriggerChip kind={s.trigger.kind} by={s.trigger.by} />
                        </span>
                      </span>
                      <span className="flex flex-col items-end gap-1 text-[12.5px] text-muted-foreground">
                        <SessionStatusBadge status={s.status} />
                        <Ago iso={s.startedAt} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <DefinitionCard agent={agent} />
        </div>

        <div className="space-y-4">
          <SectionCard title="Health" headerBar>
            <dl className="grid grid-cols-2 gap-px bg-border">
              {[
                ['Last session', agent.health.lastRun ? <Ago iso={agent.health.lastRun} /> : '—'],
                ['Sessions, 7d', agent.health.sessions7d],
                ['Failed, 7d', agent.health.failed7d],
                ['Spend, 7d', usd(agent.health.spend7d)],
              ].map(([l, v]) => (
                <div key={String(l)} className="bg-card px-4 py-3">
                  <dt className="text-[12px] text-muted-foreground">{l}</dt>
                  <dd className="font-mono text-[15px]">{v}</dd>
                </div>
              ))}
            </dl>
          </SectionCard>
          <SectionCard title="Triggers" headerBar>
            <ul className="divide-y divide-border">
              {agent.triggers.map((t) => (
                <li key={t.detail} className="px-4 py-2.5">
                  <TriggerChip kind={t.kind} by={t.detail} />
                </li>
              ))}
            </ul>
          </SectionCard>
          <SectionCard title="Scope and authority" headerBar>
            <div className="space-y-3 px-4 py-3 text-[13px]">
              <div>
                <div className="text-[12px] text-muted-foreground">Scope</div>
                <div>{agent.scope}</div>
              </div>
              <div>
                <div className="text-[12px] text-muted-foreground">Grants</div>
                <ul className="list-disc pl-4">
                  {agent.grants.map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[12px] text-muted-foreground">Gated actions</div>
                {agent.gated.length === 0 ? (
                  <div className="text-muted-foreground">
                    None — every action this agent takes is ungated.
                  </div>
                ) : (
                  <ul className="list-disc pl-4">
                    {agent.gated.map((k) => (
                      <li key={k}>
                        {kindById(k).label} ·{' '}
                        <Link to="/agents?tab=configuration" className="text-link hover:underline">
                          needs approval
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[12px] text-muted-foreground">Tools</div>
                <div className="flex flex-wrap gap-1">
                  {agent.tools.map((t) => (
                    <span
                      key={t}
                      className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11.5px]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function SessionLine({ session }: { session: SessionDef }) {
  const step = useCycle(session.liveSteps ?? [], 3000);
  if (session.status === 'running' && step) return <LiveStepLine text={step} className="py-0.5" />;
  return (
    <span className="block truncate text-[12.5px] text-muted-foreground">{session.summary}</span>
  );
}
